(() => {

  /* ========================================================
     HELPERS
     ======================================================== */

  const clamp = (value, min = 0, max = 1) => {
    return Math.min(Math.max(value, min), max);
  };

  const lerp = (start, end, progress) => {
    return start + (end - start) * progress;
  };

  const mapRange = (
    value,
    inputStart,
    inputEnd,
    outputStart,
    outputEnd
  ) => {

    const progress = clamp(
      (value - inputStart) /
      (inputEnd - inputStart)
    );

    return lerp(
      outputStart,
      outputEnd,
      progress
    );
  };


  /* ========================================================
     ENGINE
     ======================================================== */

  class BTOExperience {

    constructor(section) {

      this.section = section;

      this.raf = null;

      this.reveals =
        [...section.querySelectorAll('[data-bto-reveal]')];

      this.pinned =
        [...section.querySelectorAll('[data-bto-pinned]')];

      this.videos =
        [...section.querySelectorAll('[data-bto-video-scrub]')];

      this.zooms =
        [...section.querySelectorAll('[data-bto-zoom]')];

      this.masks =
        [...section.querySelectorAll('[data-bto-mask]')];

      this.depths =
        [...section.querySelectorAll('[data-bto-depth]')];

      this.horizontals =
        [...section.querySelectorAll('[data-bto-horizontal]')];

      this.stacks =
        [...section.querySelectorAll('[data-bto-stack]')];

      this.splits =
        [...section.querySelectorAll('[data-bto-split]')];

      this.stickyTexts =
        [...section.querySelectorAll('[data-bto-sticky-text]')];

      this.flySections =
        [...section.querySelectorAll('[data-bto-fly]')];

      this.crossfades =
        [...section.querySelectorAll('[data-bto-crossfade]')];

      this.editorials =
        [...section.querySelectorAll('[data-bto-editorial]')];

      this.rotates =
        [...section.querySelectorAll('[data-bto-rotate]')];

      this.blurs =
        [...section.querySelectorAll('[data-bto-blur]')];

      this.scaleSequences =
        [...section.querySelectorAll('[data-bto-scale-sequence]')];

      this.finals =
        [...section.querySelectorAll('[data-bto-final]')];

      this.init();

    }


    /* ========================================================
       INIT
       ======================================================== */

    init() {

      this.prepareVideos();

      this.prepareSplitText();

      this.bindEvents();

      this.requestTick();

    }


    bindEvents() {

      this.onScroll = () => {
        this.requestTick();
      };

      this.onResize = () => {
        this.requestTick();
      };

      window.addEventListener(
        'scroll',
        this.onScroll,
        { passive: true }
      );

      window.addEventListener(
        'resize',
        this.onResize,
        { passive: true }
      );

    }


    requestTick() {

      if (this.raf) return;

      this.raf =
        requestAnimationFrame(() => {

          this.update();

          this.raf = null;

        });

    }


    getProgress(element) {

      const rect =
        element.getBoundingClientRect();

      const viewport =
        window.innerHeight ||
        document.documentElement.clientHeight;

      const scrollable =
        element.offsetHeight - viewport;

      if (scrollable <= 0) {
        return 0;
      }

      return clamp(
        -rect.top / scrollable
      );

    }


    update() {

      this.updateReveal();

      this.updatePinned();

      this.updateVideos();

      this.updateZoom();

      this.updateMask();

      this.updateDepth();

      this.updateHorizontal();

      this.updateStacks();

      this.updateSplitText();

      this.updateStickyText();

      this.updateFly();

      this.updateCrossfade();

      this.updateEditorial();

      this.updateRotate();

      this.updateBlur();

      this.updateScaleSequence();

      this.updateFinal();

    }


    /* ========================================================
       02 IMAGE REVEAL
       ======================================================== */

    updateReveal() {

      this.reveals.forEach(section => {

        const p =
          this.getProgress(section);

        const frame =
          section.querySelector('.bto-reveal__frame');

        if (!frame) return;

        const expand =
          clamp(p / .6);

        const mobile =
          window.innerWidth <= 749;

        const startWidth =
          mobile ? 70 : 30;

        const startHeight =
          mobile ? 46 : 42;

        frame.style.width =
          `${lerp(startWidth, 100, expand)}vw`;

        frame.style.height =
          `${lerp(startHeight, 100, expand)}svh`;

        frame.style.setProperty(
          '--reveal-radius',
          `${lerp(28, 0, expand)}px`
        );

        frame.style.setProperty(
          '--reveal-image-scale',
          lerp(1.22, 1, expand).toFixed(4)
        );

        const copy =
          clamp((p - .45) / .35);

        section.style.setProperty(
          '--reveal-copy-opacity',
          copy.toFixed(3)
        );

        section.style.setProperty(
          '--reveal-copy-y',
          `${lerp(70, 0, copy)}px`
        );

      });

    }


    /* ========================================================
       03 PINNED
       ======================================================== */

    updatePinned() {

      this.pinned.forEach(section => {

        const p =
          this.getProgress(section);

        section.style.setProperty(
          '--pinned-image-scale',
          lerp(1.18, 1, p).toFixed(4)
        );

        section.style.setProperty(
          '--pinned-copy-y',
          `${lerp(80, -50, p)}px`
        );

      });

    }


    /* ========================================================
       04 VIDEO SCRUB
       ======================================================== */

    prepareVideos() {

      this.videos.forEach(section => {

        const video =
          section.querySelector('video');

        if (!video) return;

        video.muted = true;
        video.playsInline = true;
        video.pause();

        try {
          video.load();
        } catch (error) {}

      });

    }


    updateVideos() {

      this.videos.forEach(section => {

        const video =
          section.querySelector('video');

        if (!video) return;

        if (
          !Number.isFinite(video.duration) ||
          video.duration <= 0
        ) {
          return;
        }

        const p =
          this.getProgress(section);

        const target =
          p *
          Math.max(
            video.duration - .05,
            0
          );

        if (
          Math.abs(
            video.currentTime - target
          ) > .025
        ) {

          try {
            video.currentTime = target;
          } catch (error) {}

        }

      });

    }


    /* ========================================================
       05 SCROLL ZOOM
       ======================================================== */

    updateZoom() {

      this.zooms.forEach(section => {

        const p =
          this.getProgress(section);

        const direction =
          section.dataset.direction;

        let scale;

        if (direction === 'in') {

          scale =
            lerp(1, 1.7, p);

        } else {

          scale =
            lerp(1.7, 1, p);

        }

        section.style.setProperty(
          '--zoom-scale',
          scale.toFixed(4)
        );

        const opacity =
          clamp(
            (p - .35) / .3
          );

        section.style.setProperty(
          '--zoom-copy-opacity',
          opacity.toFixed(3)
        );

      });

    }


    /* ========================================================
       06 MASK
       ======================================================== */

    updateMask() {

      this.masks.forEach(section => {

        const p =
          this.getProgress(section);

        const reveal =
          clamp(p / .7);

        section.style.setProperty(
          '--mask-x',
          `${lerp(35, 0, reveal)}%`
        );

        section.style.setProperty(
          '--mask-y',
          `${lerp(30, 0, reveal)}%`
        );

        section.style.setProperty(
          '--mask-radius',
          `${lerp(28, 0, reveal)}px`
        );

        section.style.setProperty(
          '--mask-copy-opacity',
          clamp(
            (p - .5) / .3
          ).toFixed(3)
        );

      });

    }


    /* ========================================================
       07 DEPTH PARALLAX
       ======================================================== */

    updateDepth() {

      this.depths.forEach(section => {

        const p =
          this.getProgress(section);

        const strength =
          Number(
            getComputedStyle(section)
              .getPropertyValue('--depth-strength')
          ) || 60;

        const centered =
          p - .5;

        section.style.setProperty(
          '--depth-bg-y',
          `${centered * strength * -0.45}px`
        );

        section.style.setProperty(
          '--depth-mid-y',
          `${centered * strength * 1.1}px`
        );

        section.style.setProperty(
          '--depth-front-y',
          `${centered * strength * 2}px`
        );

        section.style.setProperty(
          '--depth-mid-scale',
          lerp(.93, 1.06, p).toFixed(4)
        );

        section.style.setProperty(
          '--depth-front-scale',
          lerp(.9, 1.12, p).toFixed(4)
        );

      });

    }


    /* ========================================================
       08 HORIZONTAL
       ======================================================== */

    updateHorizontal() {

      this.horizontals.forEach(section => {

        const p =
          this.getProgress(section);

        const track =
          section.querySelector(
            '.bto-horizontal__track'
          );

        if (!track) return;

        const available =
          Math.max(
            track.scrollWidth -
            window.innerWidth,
            0
          );

        section.style.setProperty(
          '--horizontal-x',
          `${available * p * -1}px`
        );

      });

    }


    /* ========================================================
       09 STACK
       ======================================================== */

    updateStacks() {

      this.stacks.forEach(section => {

        const p =
          this.getProgress(section);

        const cards =
          [...section.querySelectorAll('[data-stack-card]')];

        const count =
          cards.length;

        if (!count) return;

        cards.forEach((card, index) => {

          const step =
            1 / count;

          const start =
            index * step;

          const local =
            clamp(
              (p - start) / step
            );

          const before =
            index * 7;

          const y =
            lerp(
              110 + before,
              before,
              local
            );

          const scale =
            1 - ((count - index - 1) * .025);

          const rotate =
            (index - count / 2) * 1.2;

          card.style.transform = `
            translateY(${y}px)
            scale(${scale})
            rotate(${rotate}deg)
          `;

          card.style.opacity =
            local <= .02 && index > 0
              ? 0
              : 1;

          card.style.zIndex =
            index + 1;

        });

      });

    }


    /* ========================================================
       10 SPLIT TEXT
       ======================================================== */

    prepareSplitText() {

      this.splits.forEach(section => {

        const heading =
          section.querySelector('[data-split-source]');

        if (!heading) return;

        if (heading.dataset.ready === 'true') {
          return;
        }

        const text =
          heading.textContent.trim();

        const mode =
          section.dataset.splitMode;

        heading.innerHTML = '';

        let parts;

        if (mode === 'chars') {

          parts =
            [...text];

        } else {

          parts =
            text.split(/\s+/);

        }

        parts.forEach((part, index) => {

          const span =
            document.createElement('span');

          span.className =
            'bto-split-unit';

          span.dataset.splitIndex =
            index;

          if (
            mode === 'chars' &&
            part === ' '
          ) {

            span.innerHTML = '&nbsp;';

          } else {

            span.textContent = part;

          }

          heading.appendChild(span);

        });

        heading.dataset.ready = 'true';

      });

    }


    updateSplitText() {

      this.splits.forEach(section => {

        const p =
          this.getProgress(section);

        const units =
          [...section.querySelectorAll('.bto-split-unit')];

        const count =
          units.length;

        if (!count) return;

        units.forEach((unit, index) => {

          const start =
            index / count * .6;

          const local =
            clamp(
              (p - start) / .32
            );

          unit.style.opacity =
            local.toFixed(3);

          unit.style.transform = `
            translateY(${lerp(90, 0, local)}px)
            rotateX(${lerp(55, 0, local)}deg)
          `;

        });

      });

    }


    /* ========================================================
       11 STICKY TEXT
       ======================================================== */

    updateStickyText() {

      this.stickyTexts.forEach(section => {

        const p =
          this.getProgress(section);

        section.style.setProperty(
          '--sticky-text-x',
          `${lerp(55, -55, p)}vw`
        );

        const middle =
          1 - Math.abs(
            p - .5
          ) * 2;

        section.style.setProperty(
          '--sticky-text-scale',
          lerp(.75, 1.2, middle).toFixed(4)
        );

        section.style.setProperty(
          '--sticky-image-scale',
          lerp(1.12, 1.02, p).toFixed(4)
        );

        section.style.setProperty(
          '--sticky-image-y',
          `${lerp(30, -30, p)}px`
        );

      });

    }


    /* ========================================================
       12 FLY IN
       ======================================================== */

    updateFly() {

      this.flySections.forEach(section => {

        const p =
          this.getProgress(section);

        const items =
          [...section.querySelectorAll('[data-fly-item]')];

        items.forEach((item, index) => {

          const start =
            index * .13;

          const local =
            clamp(
              (p - start) / .55
            );

          let xStart = 0;
          let yStart = 0;
          let rotateStart = 0;

          if (index === 0) {
            xStart = -120;
            yStart = 100;
            rotateStart = -16;
          }

          if (index === 1) {
            xStart = 130;
            yStart = -80;
            rotateStart = 14;
          }

          if (index === 2) {
            xStart = 30;
            yStart = 160;
            rotateStart = -8;
          }

          item.style.opacity =
            local.toFixed(3);

          item.style.transform = `
            translate3d(
              ${lerp(xStart, 0, local)}px,
              ${lerp(yStart, 0, local)}px,
              0
            )
            rotate(${lerp(rotateStart, 0, local)}deg)
            scale(${lerp(.7, 1, local)})
          `;

        });

      });

    }


    /* ========================================================
       13 CROSSFADE
       ======================================================== */

    updateCrossfade() {

      this.crossfades.forEach(section => {

        const p =
          this.getProgress(section);

        const images =
          [...section.querySelectorAll('[data-crossfade-image]')];

        const count =
          images.length;

        if (!count) return;

        const segment =
          1 / count;

        images.forEach((image, index) => {

          const center =
            (index + .5) * segment;

          const distance =
            Math.abs(
              p - center
            );

          const opacity =
            clamp(
              1 -
              distance /
              (segment * .85)
            );

          image.style.opacity =
            opacity.toFixed(3);

          image.style.transform =
            `scale(${lerp(1.08, 1, opacity)})`;

        });

      });

    }


    /* ========================================================
       14 EDITORIAL
       ======================================================== */

    updateEditorial() {

      this.editorials.forEach(section => {

        const p =
          this.getProgress(section);

        const images =
          [...section.querySelectorAll('[data-editorial-image]')];

        const centered =
          p - .5;

        images.forEach((image, index) => {

          const direction =
            index % 2 === 0
              ? 1
              : -1;

          const intensity =
            70 + index * 35;

          const y =
            centered *
            intensity *
            direction;

          const rotate =
            centered *
            (index + 1) *
            5 *
            direction;

          image.style.transform = `
            translate3d(
              0,
              ${y}px,
              0
            )
            rotate(${rotate}deg)
          `;

        });

      });

    }


    /* ========================================================
       15 ROTATE
       ======================================================== */

    updateRotate() {

      this.rotates.forEach(section => {

        const p =
          this.getProgress(section);

        section.style.setProperty(
          '--rotate-z',
          `${lerp(-22, 18, p)}deg`
        );

        section.style.setProperty(
          '--rotate-y',
          `${lerp(-35, 35, p)}deg`
        );

        section.style.setProperty(
          '--rotate-scale',
          lerp(.72, 1.05, p).toFixed(4)
        );

      });

    }


    /* ========================================================
       16 BLUR
       ======================================================== */

    updateBlur() {

      this.blurs.forEach(section => {

        const p =
          this.getProgress(section);

        const sharp =
          clamp(p / .65);

        section.style.setProperty(
          '--blur-value',
          `${lerp(30, 0, sharp)}px`
        );

        section.style.setProperty(
          '--blur-scale',
          lerp(1.16, 1, sharp).toFixed(4)
        );

        section.style.setProperty(
          '--blur-image-opacity',
          lerp(.45, 1, sharp).toFixed(3)
        );

        section.style.setProperty(
          '--blur-copy-opacity',
          clamp(
            (p - .4) / .35
          ).toFixed(3)
        );

      });

    }


    /* ========================================================
       17 SCALE SEQUENCE
       ======================================================== */

    updateScaleSequence() {

      this.scaleSequences.forEach(section => {

        const p =
          this.getProgress(section);

        const images =
          [...section.querySelectorAll('[data-sequence-image]')];

        const count =
          images.length;

        if (!count) return;

        const segment =
          1 / count;

        images.forEach((image, index) => {

          const start =
            index * segment;

          const local =
            clamp(
              (p - start) / segment
            );

          let opacity;

          if (local < .5) {

            opacity =
              mapRange(
                local,
                0,
                .5,
                0,
                1
              );

          } else {

            opacity =
              index === count - 1
                ? 1
                : mapRange(
                    local,
                    .5,
                    1,
                    1,
                    0
                  );

          }

          const scale =
            lerp(
              .72,
              1.18,
              local
            );

          image.style.opacity =
            opacity.toFixed(3);

          image.style.transform =
            `scale(${scale})`;

        });

      });

    }


    /* ========================================================
       18 FINAL
       ======================================================== */

    updateFinal() {

      this.finals.forEach(section => {

        const p =
          this.getProgress(section);

        section.style.setProperty(
          '--final-image-scale',
          lerp(1.25, 1, p).toFixed(4)
        );

        section.style.setProperty(
          '--final-blur',
          `${lerp(12, 0, p)}px`
        );

        const reveal =
          clamp(
            (p - .28) / .4
          );

        section.style.setProperty(
          '--final-copy-opacity',
          reveal.toFixed(3)
        );

        section.style.setProperty(
          '--final-copy-y',
          `${lerp(80, 0, reveal)}px`
        );

      });

    }


    /* ========================================================
       DESTROY
       ======================================================== */

    destroy() {

      window.removeEventListener(
        'scroll',
        this.onScroll
      );

      window.removeEventListener(
        'resize',
        this.onResize
      );

      if (this.raf) {

        cancelAnimationFrame(
          this.raf
        );

      }

    }

  }


  /* ========================================================
     SHOPIFY INITIALIZATION
     ======================================================== */

  const instances =
    new WeakMap();


  const initialize =
    section => {

      if (!section) return;

      if (instances.has(section)) {
        return;
      }

      const instance =
        new BTOExperience(section);

      instances.set(
        section,
        instance
      );

    };


  const initializeAll =
    () => {

      document
        .querySelectorAll('[data-bto-section]')
        .forEach(initialize);

    };


  if (
    document.readyState === 'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initializeAll
    );

  } else {

    initializeAll();

  }


  /* ========================================================
     SHOPIFY THEME EDITOR
     ======================================================== */

  document.addEventListener(
    'shopify:section:load',
    event => {

      let section = null;

      if (
        event.target.matches &&
        event.target.matches('[data-bto-section]')
      ) {

        section =
          event.target;

      } else {

        section =
          event.target.querySelector(
            '[data-bto-section]'
          );

      }

      initialize(section);

    }
  );

})();